// Public overview: every tested model in a sortable table, with the benchmark
// hardware / Ollama version and any published model benchmark scores.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ColorMetric, type Overview as OverviewData, type OverviewEntry } from '../api';
import { METRIC_LABEL, formatMetric } from '../lib/colorScale';
import QuadrantChart from '../components/QuadrantChart';

type SortKey = 'model' | 'lastTestedAt' | 'score';
type View = 'graph' | 'table';
const VIEW_KEY = 'aiperf.overviewView';

function initialView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'graph';
  } catch {
    return 'graph';
  }
}

function statusClass(s: string | null): string {
  if (s === 'done') return 'ok';
  if (s === 'error') return 'err';
  if (s === 'running' || s === 'scheduled') return 'run';
  return '';
}

export default function Overview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'score', dir: -1 });
  const [view, setView] = useState<View>(initialView);

  const switchView = (v: View) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    api
      .overview()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
  }, []);

  const rows: OverviewEntry[] = data?.models ?? [];
  const metric: ColorMetric = data?.colorMetric ?? 'generate_tps';

  // Union of benchmark labels present on any model, in first-seen order.
  const benchLabels = useMemo(() => {
    const seen: string[] = [];
    for (const r of rows) {
      for (const k of Object.keys(r.benchmarks ?? {})) if (!seen.includes(k)) seen.push(k);
    }
    return seen;
  }, [rows]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -sort.dir : av > bv ? sort.dir : 0;
    });
    return copy;
  }, [rows, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: -1 }));

  if (error) return <div className="banner error">{error}</div>;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Tested models</h1>
          <p className="muted small">
            Hardware: {data?.hardware ?? '—'} · Ollama {data?.ollamaVersion ?? '—'}
            {benchLabels.length > 0 && data?.benchmarksFetchedAt
              ? ` · benchmarks updated ${new Date(data.benchmarksFetchedAt).toLocaleDateString()}`
              : ''}
          </p>
        </div>
        <Link className="btn" to="/run">
          Run tests →
        </Link>
      </div>

      <div className="view-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={view === 'graph'}
          className={view === 'graph' ? 'active' : ''}
          onClick={() => switchView('graph')}
        >
          Quadrant
        </button>
        <button
          role="tab"
          aria-selected={view === 'table'}
          className={view === 'table' ? 'active' : ''}
          onClick={() => switchView('table')}
        >
          Table
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="muted">No models tested yet.</p>
      ) : view === 'graph' ? (
        <QuadrantChart models={rows} benchLabels={benchLabels} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort('model')}>
                  Model
                </th>
                <th>Max KV</th>
                <th className="sortable" onClick={() => toggleSort('lastTestedAt')}>
                  Last tested
                </th>
                <th>Status</th>
                <th className="sortable num" onClick={() => toggleSort('score')}>
                  {METRIC_LABEL[metric]}
                </th>
                {benchLabels.map((b) => {
                  const link = data?.benchmarkLinks?.[b];
                  return (
                    <th key={b} className="num" title="Published benchmark score">
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer noopener">
                          {b}
                        </a>
                      ) : (
                        b
                      )}
                    </th>
                  );
                })}
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.model}>
                  <td>
                    <Link to={`/model/${encodeURIComponent(r.model)}`}>{r.displayName}</Link>
                    {r.displayName !== r.model ? (
                      <div className="muted small">{r.model}</div>
                    ) : null}
                  </td>
                  <td>{r.maxKv ?? '–'}</td>
                  <td>{r.lastTestedAt ? new Date(r.lastTestedAt).toLocaleString() : '–'}</td>
                  <td>
                    <span className={`badge ${statusClass(r.lastStatus)}`}>
                      {r.lastStatus ?? 'never'}
                    </span>
                  </td>
                  <td className="num">{formatMetric(r.score, metric)}</td>
                  {benchLabels.map((b) => {
                    const v = r.benchmarks?.[b];
                    return (
                      <td key={b} className="num">
                        {typeof v === 'number' ? `${v.toFixed(1)}%` : '–'}
                      </td>
                    );
                  })}
                  <td>
                    <Link
                      className="btn ghost small"
                      to={`/model/${encodeURIComponent(r.model)}`}
                    >
                      Matrix
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
